import {
  Expense,
  CreateExpenseInput,
  UpdateExpenseInput,
  ExpenseConnection,
  ExpenseStatus,
} from '../types';
import { ExpenseService } from './expense';
import { mockExpenseService } from './mockExpense';
import { GraphQLExpenseService } from './graphqlExpense';
import { isDemoMode } from './mockData';
import { isAmplifyMock } from '../config/amplifyConfig';

/**
 * Check if we should use GraphQL API (Amplify Mock or real AppSync)
 */
const shouldUseGraphQL = (): boolean => {
  // Use GraphQL if Amplify mock is running or in production (not demo mode)
  return isAmplifyMock() || !isDemoMode();
};
const EXPENSE_CACHE_TTL_MS = 30000;
const expenseConnectionCache = new Map<string, { timestamp: number; data: ExpenseConnection }>();

function getCachedExpenseConnection(key: string): ExpenseConnection | null {
  const cached = expenseConnectionCache.get(key);
  if (!cached || Date.now() - cached.timestamp > EXPENSE_CACHE_TTL_MS) return null;
  return cached.data;
}

function setCachedExpenseConnection(key: string, data: ExpenseConnection): void {
  expenseConnectionCache.set(key, { timestamp: Date.now(), data });
}

function clearExpenseConnectionCache(): void {
  expenseConnectionCache.clear();
}

/**
 * Expense Service Wrapper that routes to:
 * - GraphQL API (when Amplify mock is running or in production)
 * - Mock localStorage service (when in demo mode without Amplify mock)
 */
export class ExpenseServiceWrapper {
  static async getMyExpenses(
    userId?: string,
    userEmail?: string,
    options: { limit?: number; nextToken?: string } = {}
  ): Promise<ExpenseConnection> {
    const cacheKey = `my|${userId || ''}|${userEmail || ''}|${options.limit || ''}|${options.nextToken || ''}`;
    const cached = getCachedExpenseConnection(cacheKey);
    if (cached) return cached;
    let result: ExpenseConnection;
    if (shouldUseGraphQL() && (userId || userEmail)) {
      console.log('[Wrapper] Using GraphQL for getMyExpenses');
      result = await GraphQLExpenseService.getMyExpenses(userId, userEmail, options);
      setCachedExpenseConnection(cacheKey, result);
      return result;
    }
    if (isDemoMode()) {
      result = await mockExpenseService.getMyExpenses(userId);
      setCachedExpenseConnection(cacheKey, result);
      return result;
    }
    result = await ExpenseService.getMyExpenses();
    setCachedExpenseConnection(cacheKey, result);
    return result;
  }

  static async getTeamExpenses(managerId?: string, managerEmail?: string): Promise<ExpenseConnection> {
    const cacheKey = `team|${managerId || ''}|${managerEmail || ''}`;
    const cached = getCachedExpenseConnection(cacheKey);
    if (cached) {
      console.log('[PROFILE][ExpenseWrapper] getTeamExpenses CACHE HIT', { managerId, managerEmail, itemCount: cached.items.length });
      return cached;
    }
    const t0 = performance.now();
    console.log('[PROFILE][ExpenseWrapper] ═══════════════════════════════════════');
    console.log('[PROFILE][ExpenseWrapper] getTeamExpenses START', { managerId, managerEmail, path: managerEmail ? 'direct-report' : 'resolver' });
    let result: ExpenseConnection;
    if (shouldUseGraphQL()) {
      // Use the optimized direct-report lookup path directly.
      // The AppSync resolver path (ExpenseService.getTeamExpenses) fails because the
      // byDepartment GSI requires user.department which is often missing from Cognito claims.
      // Going straight to the manager-email lookup avoids the wasted resolver round-trip.
      if (managerEmail) {
        result = await GraphQLExpenseService.getTeamExpenses(managerEmail);
        const duration = Math.round(performance.now() - t0);
        console.log('[PROFILE][ExpenseWrapper] getTeamExpenses DONE (direct-report path)', { durationMs: duration, itemCount: result.items.length });
        console.log('[PROFILE][ExpenseWrapper] ═══════════════════════════════════════');
        setCachedExpenseConnection(cacheKey, result);
        return result;
      }
      // If no managerEmail available, try the resolver as last resort
      try {
        console.log('[PROFILE][ExpenseWrapper] getTeamExpenses START (resolver path - no managerEmail)', { managerId });
        result = await ExpenseService.getTeamExpenses();
        const duration = Math.round(performance.now() - t0);
        console.log('[PROFILE][ExpenseWrapper] getTeamExpenses DONE (resolver path)', { durationMs: duration, itemCount: result.items.length });
        setCachedExpenseConnection(cacheKey, result);
        return result;
      } catch (resolverError) {
        console.warn('[PROFILE][ExpenseWrapper] getTeamExpenses resolver FAILED (no fallback available)', { durationMs: Math.round(performance.now() - t0), error: (resolverError as any)?.message });
        throw resolverError;
      }
    }
    if (isDemoMode()) {
      result = await mockExpenseService.getTeamExpenses(managerId || '');
      setCachedExpenseConnection(cacheKey, result);
      return result;
    }
    result = await ExpenseService.getTeamExpenses();
    setCachedExpenseConnection(cacheKey, result);
    return result;
  }

  static async getAllExpenses(userRole?: string): Promise<ExpenseConnection> {
    const cacheKey = `all|${userRole || 'unknown'}`;
    const cached = getCachedExpenseConnection(cacheKey);
    if (cached) {
      console.log('[ExpenseServiceWrapper][GetAllExpenses] cache hit', {
        userRole,
        recordsReturned: cached.items.length,
      });
      return cached;
    }
    let result: ExpenseConnection;
    if (shouldUseGraphQL()) {
      console.log('[Wrapper] Using GraphQL for getAllExpenses');
      result = await GraphQLExpenseService.getAllExpenses(userRole);
      setCachedExpenseConnection(cacheKey, result);
      return result;
    }
    if (isDemoMode()) {
      result = await mockExpenseService.getAllExpenses();
      setCachedExpenseConnection(cacheKey, result);
      return result;
    }
    result = await ExpenseService.getAllExpenses();
    setCachedExpenseConnection(cacheKey, result);
    return result;
  }

  static async getPendingFinanceApproval(): Promise<ExpenseConnection> {
    if (shouldUseGraphQL()) {
      console.log('[Wrapper] Using GraphQL for getPendingFinanceApproval');
      return GraphQLExpenseService.getPendingFinanceApproval();
    }
    if (isDemoMode()) {
      return mockExpenseService.getPendingFinanceApproval();
    }
    const all = await ExpenseService.getAllExpenses();
    return {
      items: all.items.filter(
        (e) => e.status === ExpenseStatus.MANAGER_APPROVED
      ),
      total: all.items.filter((e) => e.status === ExpenseStatus.MANAGER_APPROVED)
        .length,
    };
  }

  static async createDraft(
    input: CreateExpenseInput,
    userId?: string,
    userName?: string,
    userDepartment?: string
  ): Promise<Expense> {
    console.log('[ExpenseServiceWrapper] ========== CREATE DRAFT ==========');
    console.log('[ExpenseServiceWrapper] Input:', input);
    console.log('[ExpenseServiceWrapper] User ID:', userId);
    console.log('[ExpenseServiceWrapper] User name:', userName);
    console.log('[ExpenseServiceWrapper] User department:', userDepartment);
    console.log('[ExpenseServiceWrapper] Should use GraphQL:', shouldUseGraphQL());
    console.log('[ExpenseServiceWrapper] Is demo mode:', isDemoMode());
    
    if (shouldUseGraphQL() && userId) {
      console.log('[Wrapper] Using GraphQL for createDraft');
      const result = await GraphQLExpenseService.createDraft(input, userId, userName, userDepartment);
      console.log('[ExpenseServiceWrapper] GraphQL result:', result);
      clearExpenseConnectionCache();
      return result;
    }
    if (isDemoMode()) {
      console.log('[Wrapper] Using mock service for createDraft');
      return mockExpenseService.createDraft(input, userId || 'demo-user');
    }
    console.log('[Wrapper] Using ExpenseService for createDraft');
    return ExpenseService.createDraft(input);
  }

  static async updateDraft(
    id: string,
    input: UpdateExpenseInput
  ): Promise<Expense> {
    if (shouldUseGraphQL()) {
      console.log('[Wrapper] Using GraphQL for updateDraft');
      const result = await GraphQLExpenseService.updateExpense(id, input);
      clearExpenseConnectionCache();
      return result;
    }
    if (isDemoMode()) {
      return mockExpenseService.updateDraft(id, input);
    }
    return ExpenseService.updateDraft(id, input);
  }

  static async submitForApproval(id: string, submitterInfo?: { role?: string; managerEmail?: string }): Promise<Expense> {
    console.log('[ExpenseServiceWrapper] ========== SUBMIT FOR APPROVAL ==========');
    console.log('[ExpenseServiceWrapper] Expense ID:', id);
    console.log('[ExpenseServiceWrapper] Submitter info:', submitterInfo);
    console.log('[ExpenseServiceWrapper] Should use GraphQL:', shouldUseGraphQL());
    console.log('[ExpenseServiceWrapper] Is demo mode:', isDemoMode());
    
    if (shouldUseGraphQL()) {
      console.log('[Wrapper] Using GraphQL for submitForApproval');
      const result = await GraphQLExpenseService.submitForApproval(id, submitterInfo);
      console.log('[ExpenseServiceWrapper] GraphQL result:', result);
      clearExpenseConnectionCache();
      return result;
    }
    if (isDemoMode()) {
      console.log('[Wrapper] Using mock service for submitForApproval');
      return mockExpenseService.submitForApproval(id);
    }
    console.log('[Wrapper] Using ExpenseService for submitForApproval');
    return ExpenseService.submitForApproval(id);
  }

  static async deleteDraft(id: string): Promise<void> {
    if (shouldUseGraphQL()) {
      console.log('[Wrapper] Using GraphQL for deleteDraft');
      await GraphQLExpenseService.deleteDraft(id);
      clearExpenseConnectionCache();
      return;
    }
    if (isDemoMode()) {
      return mockExpenseService.deleteDraft(id);
    }
    return ExpenseService.deleteDraft(id);
  }

  // Approval methods
  static async approveExpense(
    id: string,
    approverId: string,
    comments?: string
  ): Promise<Expense> {
    if (shouldUseGraphQL()) {
      console.log('[Wrapper] Using GraphQL for approveExpense');
      const result = await GraphQLExpenseService.approveExpense(id, approverId, comments);
      clearExpenseConnectionCache();
      return result;
    }
    if (isDemoMode()) {
      return mockExpenseService.approveExpense(id, approverId, comments);
    }
    throw new Error('Real approval service not implemented in wrapper');
  }

  static async rejectExpense(
    id: string,
    approverId: string,
    comments: string
  ): Promise<Expense> {
    if (shouldUseGraphQL()) {
      console.log('[Wrapper] Using GraphQL for rejectExpense');
      const result = await GraphQLExpenseService.rejectExpense(id, approverId, comments);
      clearExpenseConnectionCache();
      return result;
    }
    if (isDemoMode()) {
      return mockExpenseService.rejectExpense(id, approverId, comments);
    }
    throw new Error('Real rejection service not implemented in wrapper');
  }

  static async markReimbursed(id: string): Promise<Expense> {
    if (shouldUseGraphQL()) {
      console.log('[Wrapper] Using GraphQL for markReimbursed');
      const result = await GraphQLExpenseService.markReimbursed(id);
      clearExpenseConnectionCache();
      return result;
    }
    if (isDemoMode()) {
      return mockExpenseService.markReimbursed(id);
    }
    throw new Error('Real reimbursement service not implemented in wrapper');
  }

  // New methods for manager expense creation
  static async createExpense(
    input: CreateExpenseInput,
    userId: string,
    userName?: string,
    userRole?: string,
    userManagerEmail?: string,
    userDepartment?: string
  ): Promise<Expense> {
    // Create and immediately submit the expense
    const draft = await this.createDraft(input, userId, userName, userDepartment);
    
    // Pass user info to determine if manager approval should be bypassed
    return this.submitForApproval(draft.id, { role: userRole, managerEmail: userManagerEmail });
  }

  static async saveDraft(
    input: CreateExpenseInput,
    userId: string,
    userName?: string,
    userDepartment?: string
  ): Promise<Expense> {
    return this.createDraft(input, userId, userName, userDepartment);
  }

  // Reset demo data
  static resetDemoData(): void {
    if (isDemoMode()) {
      mockExpenseService.resetDemoData();
    }
  }

  // Delegate static utility methods to original service
  static canEdit(expense: Expense): boolean {
    return ExpenseService.canEdit(expense);
  }

  static canSubmit(expense: Expense): boolean {
    return ExpenseService.canSubmit(expense);
  }

  static canDelete(expense: Expense): boolean {
    return ExpenseService.canDelete(expense);
  }

  /**
   * Delete any expense (for super admin use)
   */
  static async deleteExpense(id: string): Promise<void> {
    if (shouldUseGraphQL()) {
      console.log('[Wrapper] Using GraphQL for deleteExpense');
      return GraphQLExpenseService.deleteDraft(id);
    }
    if (isDemoMode()) {
      return mockExpenseService.deleteDraft(id);
    }
    return ExpenseService.deleteDraft(id);
  }

  static validateForSubmission(expense: Expense): {
    isValid: boolean;
    errors: string[];
  } {
    return ExpenseService.validateForSubmission(expense);
  }

  static getStatusColor(status: ExpenseStatus): string {
    return ExpenseService.getStatusColor(status);
  }

  static getStatusDescription(status: ExpenseStatus): string {
    return ExpenseService.getStatusDescription(status);
  }

  static formatAmount(amount: number): string {
    return ExpenseService.formatAmount(amount);
  }
}
